import { FavoriteError, FavoriteErrorCode } from '@/utils/favorites/errors';

// URL parsing needs an absolute base, so we use a dummy internal origin for app-relative paths.
const DUMMY_INTERNAL_BASE_URL = 'https://hub.internal';

const SENSITIVE_QUERY_PARAMS = new Set([
  'access_token',
  'code',
  'email',
  'id_token',
  'refresh_token',
  'session',
  'token',
]);

function toURL(path: string): URL {
  if (typeof path !== 'string' || path.trim().length === 0) {
    throw new FavoriteError(
      'Favorite path is required.',
      FavoriteErrorCode.PATH_REQUIRED
    );
  }

  if (!path.startsWith('/') || path.startsWith('//')) {
    throw new FavoriteError(
      'Favorite path must be an internal relative URL.',
      FavoriteErrorCode.PATH_NOT_INTERNAL_RELATIVE
    );
  }

  return new URL(path, DUMMY_INTERNAL_BASE_URL);
}

export function normalizeFavoritePath(path: string): string {
  const url = toURL(path.trim());

  for (const [key] of url.searchParams.entries()) {
    if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
      url.searchParams.delete(key);
    }
  }

  const normalizedSearch = url.searchParams.toString();
  return `${url.pathname}${normalizedSearch ? `?${normalizedSearch}` : ''}`;
}

export function getFavoritePathname(path: string): string {
  return toURL(normalizeFavoritePath(path)).pathname;
}

export function stripFavoriteSearch(path: string): string {
  return getFavoritePathname(path);
}

export function favoritePathsMatch(
  favoritePath: string,
  currentPath: string
): boolean {
  const normalizedFavoritePath = normalizeFavoritePath(favoritePath);
  const normalizedCurrentPath = normalizeFavoritePath(currentPath);

  if (normalizedFavoritePath === normalizedCurrentPath) {
    return true;
  }

  const favoritePathname = getFavoritePathname(normalizedFavoritePath);
  const currentPathname = getFavoritePathname(normalizedCurrentPath);

  return favoritePathname === currentPathname;
}
